import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, List, LayoutGrid, Building2, Settings2 } from 'lucide-react';
import { customerSupportService } from '@/services/customerSupportService';
import {
  ClientImplementation,
  KanbanData,
  ImplementationPhase,
  PHASE_LABELS,
  CreateImplementationDTO
} from '@/types/customerSupport';
import { DndContext, DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import ImplementationKanbanColumn from '@/components/customer-support/ImplementationKanbanColumn';
import ImplementationCard from '@/components/customer-support/ImplementationCard';
import ImplementationWizard from '@/components/customer-support/ImplementationWizard';
import ImplementationObservations from '@/components/customer-support/ImplementationObservations';
import KanbanColumnsConfigDialog from '@/components/customer-support/KanbanColumnsConfigDialog';
import { useKanbanColumnConfig } from '@/hooks/useKanbanColumnConfig';
import { apiService } from '@/services/api';

const CustomerSupport = () => {
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [kanbanData, setKanbanData] = useState<KanbanData>({});
  const [implementations, setImplementations] = useState<ClientImplementation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [selectedImplementation, setSelectedImplementation] = useState<ClientImplementation | null>(null);
  const [showObservations, setShowObservations] = useState(false);
  const [activeImplementation, setActiveImplementation] = useState<ClientImplementation | null>(null);
  const [showKanbanConfig, setShowKanbanConfig] = useState(false);

  const {
    columns: kanbanColumnsConfig,
    visibleColumns: visibleKanbanColumns,
    columnsByKey,
    loading: kanbanColumnsLoading,
    moveUp: moveColumnUp,
    moveDown: moveColumnDown,
    toggleVisible: toggleColumnVisible,
    update: updateKanbanColumn,
    create: createKanbanColumn,
    remove: deleteKanbanColumn,
    reload: reloadKanbanColumns,
  } = useKanbanColumnConfig();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Carregar dados
  const loadData = async () => {
    try {
      setIsLoading(true);
      const [kanban, list] = await Promise.all([
        customerSupportService.getKanban(),
        customerSupportService.getImplementations()
      ]);
      setKanbanData(kanban);
      setImplementations(list);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar implantações',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handlers DnD
  const handleDragStart = (event: DragStartEvent) => {
    // Implementar se necessário
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const implementationId = active.id as number;
    const newPhase = String(over.id);

    // Encontrar implantação atual
    let implementation: ClientImplementation | null = null;
    let currentPhase: string | null = null;

    for (const phase of Object.keys(kanbanData)) {
      const found = kanbanData[phase].find(i => i.id === implementationId);
      if (found) {
        implementation = found;
        currentPhase = phase;
        break;
      }
    }

    if (!implementation || !currentPhase || currentPhase === newPhase) return;

    try {
      await customerSupportService.movePhase(implementationId, newPhase);

      setKanbanData(prev => {
        const newData = { ...prev };
        newData[currentPhase!] = newData[currentPhase!].filter(i => i.id !== implementationId);
        newData[newPhase] = [...(newData[newPhase] || []), { ...implementation!, current_phase: newPhase as ImplementationPhase }];
        return newData;
      });

      const targetLabel = columnsByKey[newPhase]?.label
        ?? PHASE_LABELS[newPhase as ImplementationPhase]
        ?? newPhase;
      toast({
        title: 'Sucesso',
        description: `Implantação movida para ${targetLabel}`
      });
    } catch (error: any) {
      console.error('Erro ao mover implantação:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível mover a implantação',
        variant: 'destructive'
      });
    }
  };

  const handleImplementationClick = async (implementation: ClientImplementation) => {
    // Carregar dados completos da implantação incluindo agendamentos
    try {
      const fullImplementation = await customerSupportService.getImplementation(implementation.id);
      setSelectedImplementation(fullImplementation);
      setShowWizard(true);
      setActiveImplementation(fullImplementation);
    } catch (error) {
      console.error('Erro ao carregar implantação:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados da implantação',
        variant: 'destructive'
      });
    }
  };

  const handleViewObservations = async (implementation: ClientImplementation) => {
    try {
      const fullImplementation = await customerSupportService.getImplementation(implementation.id);
      setSelectedImplementation(fullImplementation);
      setShowObservations(true);
      setActiveImplementation(fullImplementation);
    } catch (error) {
      console.error('Erro ao carregar observações:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar observações',
        variant: 'destructive'
      });
    }
  };

  const handleCreateNew = () => {
    setSelectedImplementation(null);
    setShowWizard(true);
  };

  const handleWizardSave = async (
    data: CreateImplementationDTO,
    options?: { keepOpen?: boolean }
  ) => {
    try {
      let savedId = selectedImplementation?.id;

      if (selectedImplementation?.id) {
        await customerSupportService.updateImplementation(selectedImplementation.id, data);
      } else {
        if (data.client_id) {
          const existingImplementations = await customerSupportService.getImplementations({
            client_id: data.client_id,
          });

          if (existingImplementations && existingImplementations.length > 0) {
            const existing = existingImplementations[0];
            await customerSupportService.updateImplementation(existing.id, data);
            savedId = existing.id;
          } else {
            const created = await customerSupportService.createImplementation(data);
            savedId = created.id;
          }
        } else {
          await customerSupportService.createImplementation(data);
        }
      }

      await loadData();

      if (options?.keepOpen) {
        // Refresca selectedImplementation com os dados persistidos para que o
        // formulário do wizard reflita o estado real do backend.
        if (savedId) {
          const refreshed = await customerSupportService.getImplementation(savedId);
          setSelectedImplementation(refreshed);
        }
      } else {
        setShowWizard(false);
        setSelectedImplementation(null);
      }
    } catch (error: any) {
      throw error;
    }
  };

  const handleObservationCreated = (observation: any) => {
    if (activeImplementation) {
      setActiveImplementation({
        ...activeImplementation,
        observations: [...(activeImplementation.observations || []), observation]
      });
    }
    loadData();
  };

  const handleObservationUpdated = (observation: any) => {
    if (activeImplementation) {
      setActiveImplementation({
        ...activeImplementation,
        observations: (activeImplementation.observations || []).map(obs =>
          obs.id === observation.id ? observation : obs
        )
      });
    }
    loadData();
  };

  const handleObservationDeleted = (observationId: number) => {
    if (activeImplementation) {
      setActiveImplementation({
        ...activeImplementation,
        observations: (activeImplementation.observations || []).filter(obs => obs.id !== observationId)
      });
    }
    loadData();
  };

  const handleDeleteImplementation = async (id: number) => {
    try {
      await customerSupportService.deleteImplementation(id);
      toast({
        title: 'Implantação excluída',
        description: 'A implantação foi removida com sucesso.'
      });
      setShowWizard(false);
      setSelectedImplementation(null);
      setActiveImplementation(null);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao excluir implantação:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível excluir a implantação',
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Estatísticas
  const totalImplementations = Object.values(kanbanData).reduce((acc, impls) => acc + impls.length, 0);
  const blockedCount = implementations.filter(i => i.status === 'BLOCKED').length;
  const completedCount = implementations.filter(i => i.status === 'COMPLETED').length;

  // Filtrar lista
  const filteredImplementations = implementations.filter(impl => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const client = impl.client;
    return (
      client?.legal_name?.toLowerCase().includes(term) ||
      client?.trade_name?.toLowerCase().includes(term) ||
      client?.company_tax_id?.includes(term)
    );
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-3xl font-bold tracking-tight">Implantação</h2>
            <p className="text-muted-foreground">
              Gerencie as implantações de clientes
            </p>
          </div>

          <Button onClick={handleCreateNew} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Nova Implantação
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Implantações</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalImplementations}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bloqueadas</CardTitle>
              <Badge variant="destructive">!</Badge>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{blockedCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Concluídas</CardTitle>
              <Badge variant="outline" className="bg-green-100 text-green-800">✓</Badge>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{completedCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
              <Badge variant="outline">→</Badge>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalImplementations - completedCount - blockedCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e View Mode */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar implantações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Kanban
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-2" />
              Lista
            </Button>
            {viewMode === 'kanban' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowKanbanConfig(true)}
                title="Editar colunas do Kanban"
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Editar Kanban
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {viewMode === 'kanban' ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 overflow-x-auto pb-4">
              {visibleKanbanColumns.length === 0 ? (
                <div className="w-full text-center text-muted-foreground py-12">
                  Nenhuma coluna ativa. Use <strong>Editar Kanban</strong> para mostrar ou criar colunas.
                </div>
              ) : (
                visibleKanbanColumns.map(column => (
                  <ImplementationKanbanColumn
                    key={column.id}
                    column={column}
                    columnsByKey={columnsByKey}
                    implementations={kanbanData[column.key] || []}
                    onImplementationClick={handleImplementationClick}
                    onViewObservations={handleViewObservations}
                    searchTerm={searchTerm}
                  />
                ))
              )}
            </div>
          </DndContext>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredImplementations.map(implementation => (
              <ImplementationCard
                key={implementation.id}
                implementation={implementation}
                columnsByKey={columnsByKey}
                onClick={() => handleImplementationClick(implementation)}
              />
            ))}
            {filteredImplementations.length === 0 && (
              <div className="col-span-full text-center text-muted-foreground py-8">
                Nenhuma implantação encontrada
              </div>
            )}
          </div>
        )}

        {/* Wizard Modal */}
        <Dialog open={showWizard} onOpenChange={setShowWizard}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedImplementation ? 'Editar Implantação' : 'Nova Implantação'}
              </DialogTitle>
              <DialogDescription>
                Preencha os dados da implantação seguindo os 5 passos do wizard
              </DialogDescription>
            </DialogHeader>
            <ImplementationWizard
              clientId={selectedImplementation?.client_id}
              implementation={selectedImplementation || undefined}
              onSave={handleWizardSave}
              onCancel={() => {
                setShowWizard(false);
                setSelectedImplementation(null);
              }}
              onDelete={handleDeleteImplementation}
              kanbanColumns={kanbanColumnsConfig}
            />
          </DialogContent>
        </Dialog>

        {/* Kanban Columns Config */}
        <KanbanColumnsConfigDialog
          open={showKanbanConfig}
          onOpenChange={(v) => {
            setShowKanbanConfig(v);
            // ao fechar, recarregar dados do kanban para refletir colunas novas/removidas
            if (!v) {
              reloadKanbanColumns();
              loadData();
            }
          }}
          columns={kanbanColumnsConfig}
          loading={kanbanColumnsLoading}
          onMoveUp={moveColumnUp}
          onMoveDown={moveColumnDown}
          onToggleVisible={toggleColumnVisible}
          onCreate={async ({ label, color }) => {
            await createKanbanColumn({ label, color });
          }}
          onUpdate={async (id, data) => {
            await updateKanbanColumn(id, data);
          }}
          onDelete={async (id) => {
            await deleteKanbanColumn(id);
          }}
        />

        {/* Observations Modal */}
        <Dialog open={showObservations} onOpenChange={setShowObservations}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Observações - {activeImplementation?.client?.legal_name}
              </DialogTitle>
              <DialogDescription>
                Gerencie observações e comentários sobre esta implantação
              </DialogDescription>
            </DialogHeader>
            {activeImplementation && (
              <ImplementationObservations
                implementationId={activeImplementation.id}
                observations={activeImplementation.observations || []}
                onObservationCreated={handleObservationCreated}
                onObservationUpdated={handleObservationUpdated}
                onObservationDeleted={handleObservationDeleted}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CustomerSupport;

